// src/AudioManager.js
import { logger } from "./Logger.js";

export class AudioManager {
    constructor() {
        this.ctx = new (window.AudioContext || window.webkitAudioContext)();
        this.masterGain = this.ctx.createGain();
        this.masterGain.connect(this.ctx.destination);
        this.masterGain.gain.value = 0.24; // Default: 0.8 * 0.3
        
        this.sfxEnabled = true;
        this.musicEnabled = false;
        this.volume = 0.8;
        this.musicInterval = null;
    }

    async resume() {
        if (this.ctx.state === 'suspended') {
            await this.ctx.resume();
        }
    }

    playOsc(freq, type = 'sine', duration = 0.2, volume = 0.5, isMusic = false) {
        if (isMusic && !this.musicEnabled) return;
        if (!isMusic && !this.sfxEnabled) return;
        
        this.resume();
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();

        osc.type = type;
        osc.frequency.setValueAtTime(freq, this.ctx.currentTime);

        g.gain.setValueAtTime(volume, this.ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + duration);

        osc.connect(g);
        g.connect(this.masterGain);
 
        try {
            if (this.ctx.state === 'running') {
                osc.start();
                osc.stop(this.ctx.currentTime + duration);
            }
        } catch (e) {
            logger.debug("[AudioManager] OSC start failed due to context state:", this.ctx.state);
        }

        // [GC CLEANUP] Vital for multi-range mobile stability
        // Nodes must be disconnected to be eligible for garbage collection
        osc.onended = () => {
            osc.disconnect();
            g.disconnect();
        };

        // Safety fallback for browsers that don't trigger onended for stopped oscs
        setTimeout(() => {
            try {
                osc.disconnect();
                g.disconnect();
            } catch (e) { logger.debug("[AudioManager] GC cleanup retry failed (expected on some browsers)", e.message); }
        }, (duration * 1000) + 100);
    }

    playSuccess() {
        this.playOsc(440, 'sine', 0.1);
        setTimeout(() => this.playOsc(659.25, 'sine', 0.1), 100);
        setTimeout(() => this.playOsc(880, 'sine', 0.3), 200);
    }

    playFailure() {
        this.playOsc(220, 'sawtooth', 0.1, 0.3);
        setTimeout(() => this.playOsc(110, 'sawtooth', 0.4, 0.3), 100);
    }

    playLethal() {
        this.playOsc(60, 'square', 0.8, 0.4);
        this.playOsc(62, 'square', 0.8, 0.2);
    }

    playClick() {
        this.playOsc(1200, 'sine', 0.05, 0.1);
    }

    playHeartbeat(isCritical = false) {
        const freq = isCritical ? 1100 : 900; // Medical standard frequencies
        const vol = isCritical ? 0.25 : 0.12;
        this.playOsc(freq, 'sine', 0.08, vol);
    }

    playTransition() {
        this.playOsc(300, 'sine', 0.5, 0.2);
        const osc = this.ctx.createOscillator();
        osc.frequency.setValueAtTime(300, this.ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(800, this.ctx.currentTime + 0.5);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.2, this.ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.5);
        osc.connect(g);
        g.connect(this.masterGain);
        osc.start();
        osc.stop(this.ctx.currentTime + 0.5);
    }

    // =============================================
    // DYNAMIC CLINICAL MONITOR (New)
    // =============================================

    startMonitor(bpm = 75, isCritical = false) {
        this.stopMonitor();
        this.resume();

        const interval = (60 / bpm) * 1000;
        // Interval-based monitor is now disabled in favor of EKG-sync
        /*
        this.monitorInterval = setInterval(() => {
            const freq = isCritical ? 1200 : 900;
            const vol = isCritical ? 0.3 : 0.15;
            this.playOsc(freq, 'sine', 0.08, vol);
        }, interval);
        */

        if (isCritical) {
            // Critical Over-alarm (Subtle Square wave)
            this.alarmInterval = setInterval(() => {
                this.playOsc(1500, 'sine', 0.15, 0.05);
                setTimeout(() => this.playOsc(1300, 'sine', 0.15, 0.05), 200);
            }, 2000);
        }
    }

    stopMonitor() {
        if (this.monitorInterval) clearInterval(this.monitorInterval);
        if (this.alarmInterval) clearInterval(this.alarmInterval);
        this.monitorInterval = null;
        this.alarmInterval = null;
    }

    playFlatline() {
        this.stopMonitor();
        const osc = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        osc.frequency.setValueAtTime(800, this.ctx.currentTime);
        g.gain.setValueAtTime(0.2, this.ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 3.0);
        osc.connect(g);
        g.connect(this.masterGain);
        osc.start();
        osc.stop(this.ctx.currentTime + 3.0);
    }

    playGachaReveal() {
        this.resume();
        const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
        notes.forEach((freq, i) => {
            setTimeout(() => this.playOsc(freq, 'sine', 0.2, 0.2), i * 150);
        });
    }

    play(name) {
        switch(name) {
            case 'click_success': this.playSuccess(); break;
            case 'click_neutral': this.playClick(); break;
            case 'click_failure': this.playFailure(); break;
            case 'ambient_hospital': 
                this.playOsc(150, 'sine', 0.5, 0.05); 
                setTimeout(() => this.playOsc(120, 'sine', 0.5, 0.05), 1000);
                break;
            default: this.playClick();
        }
    }

    setSFXEnabled(enabled) {
        this.sfxEnabled = enabled;
        logger.info(`[Audio] SFX toggled: ${enabled}`);
    }

    setMusicEnabled(enabled) {
        this.musicEnabled = enabled;
        logger.info(`[Audio] Music toggled: ${enabled}`);
        if (enabled) {
            this.startMusic();
        } else {
            this.stopMusic();
        }
    }

    setVolume(volumeFraction) {
        this.volume = volumeFraction;
        this.masterGain.gain.value = volumeFraction * 0.3;
        logger.info(`[Audio] Volume set: ${Math.round(volumeFraction * 100)}%`);
    }

    startMusic() {
        if (this.musicInterval) return;
        this.musicInterval = setInterval(() => {
            if (!this.musicEnabled) return;
            const notes = [261.63, 293.66, 329.63, 392.00, 440.00]; // C4, D4, E4, G4, A4
            const note = notes[Math.floor(Math.random() * notes.length)];
            this.playOsc(note, 'sine', 1.8, 0.015, true);
        }, 2500);
    }

    stopMusic() {
        if (this.musicInterval) {
            clearInterval(this.musicInterval);
            this.musicInterval = null;
        }
    }
}

export const audio = new AudioManager();
